import './KickDetailPanel.scss';

import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { MeasurementMarkerDto } from 'types/models/SessionsPageData';
import BallTrajectory3DChart, { BallTrajectoryData } from 'pages/Players/charts/BallTrajectory3DChart';
import { useSessionEnrichment } from 'utilities/useSessionEnrichment';
import { toTrajectory3DFromSamples } from 'utilities/kickTrajectoryUtils';
import { measurementDetailUrl } from 'routePaths';
import { t } from 'utilities/TranslationUtils';
import { countTitle } from 'pages/MeasurementDetail/kickWording';
import KickResultChip from 'pages/Sessions/components/KickResultChip';
import PullDirectionCard from 'pages/Sessions/components/PullDirectionCard';

interface KickDetailPanelProps {
  measurement: MeasurementMarkerDto;
  /** How many of this player's kicks / throws the kick's session holds (for the "Show session" button). */
  sessionKickCount: number;
  onClose: () => void;
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className='kick-panel-metric'>
      <span className='kick-panel-metric-label'>{label}</span>
      <span className='kick-panel-metric-value'>{value}</span>
    </div>
  );
}

/**
 * Side panel for the kick picked in the table: date, video angles, the same metrics as the kick modal and a button
 * that opens its session. Replaces the stat tiles beside the table while a kick is open.
 */
const KickDetailPanel = observer(({ measurement, sessionKickCount, onClose }: KickDetailPanelProps): React.ReactElement => {
  const { apiServiceAxiosInstance: api, playerPerformanceStore: store } = useStores();
  const [videoIndex, setVideoIndex] = useState(0);
  const [view, setView] = useState<'video' | '3d'>('video');
  // Wall + targets overlay for the 3D view; fetched only once 3D is shown.
  const enrichment = useSessionEnrichment(measurement.sessionId, measurement.id, view === '3d');
  const isThrow = measurement.kind === 'ThrowIn';

  const details = store.sessionDetailsFor(measurement.sessionId, measurement.id);
  const loading = details == null;
  const videos = details?.videos ?? [];
  const currentVideo = videos[Math.max(0, Math.min(videoIndex, videos.length - 1))];

  useEffect(() => {
    setVideoIndex(0);
    store.loadSessionDetails(api, measurement.sessionId, measurement.id);
  }, [measurement.sessionId, measurement.id]);

  const trajectory = useMemo<BallTrajectoryData | null>(
    () => toTrajectory3DFromSamples({
      ...measurement,
      trajectorySamples: details?.trajectorySamples ?? measurement.trajectorySamples,
    }),
    [details, measurement],
  );

  const captured = new Date(measurement.capturedAtUtc);

  return (
    <aside className='kick-panel' data-test='pp-kick-panel'>
      <div className='kick-panel-sticky'>
        <div className='kick-panel-head'>
          <div>
            <div className='kick-panel-title'>{isThrow ? t('sessions.kickDetailTitleThrow') : t('sessions.kickDetailTitle')}</div>
            <div className='kick-panel-date'>
              {captured.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
            {measurement.result && (
              <div className='kick-panel-result' data-test='pp-kick-panel-result'>
                <KickResultChip result={measurement.result} kind={measurement.kind} onTarget={measurement.onTarget ?? null} />
              </div>
            )}
          </div>
          <button type='button' className='kick-panel-close' onClick={onClose} aria-label={t('sessions.kickModalClose')} data-test='pp-kick-panel-close'>×</button>
        </div>

        <div className='kick-panel-switch' role='tablist'>
          {(['video', '3d'] as const).map((v) => (
            <button
              key={v}
              type='button'
              role='tab'
              aria-selected={view === v}
              className={`kick-panel-switch-btn${view === v ? ' kick-panel-switch-btn--active' : ''}`}
              onClick={() => setView(v)}
              data-test={`pp-kick-panel-${v}`}
            >
              {v === 'video' ? t('playerPerformance.kickPanelVideo') : t('playerPerformance.kickPanel3D')}
            </button>
          ))}
        </div>
      </div>

      {view === '3d' ? (
        <div className='kick-panel-video kick-panel-3d' data-test='pp-kick-panel-3d-view'>
          {loading && <div className='kick-panel-video-empty'>{t('sessions.kickDetailLoading3D')}</div>}
          {!loading && !trajectory && <div className='kick-panel-video-empty'>{t('sessions.kickDetailNo3D')}</div>}
          {!loading && trajectory && (
            <BallTrajectory3DChart trajectory={trajectory} wallPlayers={enrichment?.wallPlayers} targets={enrichment?.targets} compact />
          )}
        </div>
      ) : (
      <div className='kick-panel-video'>
        {loading && <div className='kick-panel-video-empty'>{t('common.loading')}</div>}
        {!loading && videos.length === 0 && (
          <div className='kick-panel-video-empty'>{isThrow ? t('sessions.kickModalVideoUnavailableThrow') : t('sessions.kickModalVideoUnavailable')}</div>
        )}
        {currentVideo?.url && (
          <video key={currentVideo.url} src={currentVideo.url} controls playsInline poster={currentVideo.thumbnailUrl ?? undefined} />
        )}
      </div>
      )}
      {view === 'video' && videos.length > 1 && (
        <div className='kick-panel-angles'>
          {videos.map((v, i) => (
            <button
              key={v.videoId ?? i}
              type='button'
              className={`kick-panel-angle${i === videoIndex ? ' kick-panel-angle--active' : ''}`}
              onClick={() => setVideoIndex(i)}
            >
              {v.label ?? `${t('sessions.kickDetailCamera')} ${i + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* Four numbers in a 2x2 block; the pull-direction ball takes the rest of the width (below them on a narrow panel). */}
      <div className='kick-panel-data' data-test='pp-kick-panel-data'>
      <div className='kick-panel-data-grid'>
      <div className='kick-panel-metrics'>
        <Metric label={t('sessions.kickDetailBallSpeed')} value={measurement.ballSpeed != null ? `${measurement.ballSpeed.toFixed(1)} ${t('common.unitKmh')}` : '—'} />
        <Metric label={t('sessions.kickDetailHangTime')} value={measurement.hangTime != null ? `${measurement.hangTime.toFixed(2)}${t('common.unitSeconds')}` : '—'} />
        <Metric label={t('sessions.kickDetailDistance')} value={measurement.distance != null ? `${measurement.distance.toFixed(1)} ${t('common.unitMeters')}` : '—'} />
        <Metric label={t('sessions.kickDetailPosition')} value={measurement.launchAngle != null ? `${measurement.launchAngle.toFixed(1)}°` : '—'} />
      </div>
      <div className='kick-panel-pull'>
        <PullDirectionCard pullDirection={measurement.pullDirection ?? null} spinAxis={measurement.spinAxis ?? null} />
      </div>
      </div>
      </div>

      <a
        className='kick-panel-session'
        href={measurementDetailUrl(measurement.sessionId, measurement.id)}
        target='_blank'
        rel='noopener noreferrer'
        data-test='pp-kick-panel-session'
      >
        {t('playerPerformance.showSession', { title: countTitle(isThrow, sessionKickCount) })}
      </a>
    </aside>
  );
});

export default KickDetailPanel;
